// ---------------------------------------------------------------------------
// v-ec-datatable: transforma uma <table> montada pelo Vue em data-table.
//
//   <div class="table-responsive">
//       <table class="table" v-ec-datatable>...</table>
//   </div>
//
// Modo local (padrão): busca, ordenação pelo cabeçalho, filtros por coluna,
// itens por página e paginação no navegador, sobre as linhas que o Vue montou.
// Modo servidor ({ servidor: true }): a tela pagina/busca/ordena na API; a diretiva
// só cuida da exibição (colunas, densidade, cartões) e repassa filtros/exportação.
//
// Em todos os modos:
//   - "Exibição": mostrar/ocultar colunas e densidade (compacta/confortável),
//     salvos no navegador por tela;
//   - densidade compacta: uma linha por registro, textos longos com reticências e
//     o conteúdo completo no tooltip; cabeçalho e coluna de ações fixos ao rolar;
//   - "Exportar": baixa um CSV (abre no Excel) com as colunas visíveis;
//   - celular: cada linha vira um cartão (rótulo da coluna + valor).
//
// Opções:
//   busca, ordenar, paginar (true no modo local), porPagina (10)
//   servidor (false)        a API pagina; desliga busca/ordenação/paginação locais
//   colunas (true)          painel "Exibição" (colunas + densidade)
//   ocultas ([])            rótulos das colunas ocultas por padrão
//   filtros (true)          filtro no cabeçalho de cada coluna (no modo servidor só
//                           com aoFiltrar)
//   aoFiltrar(lista)        modo servidor: recebe [{ chave, rotulo, texto }] a cada mudança
//   exportar (true)         true = linhas da tabela; função ({ colunas }) =>
//                           Promise<{ cabecalho: [], linhas: [[]] }> busca tudo na API
//   nomeArquivo             nome do CSV (padrão: título da página)
//   ferramentas             seletor de um contêiner vazio onde os botões entram
//                           (padrão: uma barra própria acima da tabela)
//   id                      chave das preferências salvas (padrão: caminho da página)
//
// Colunas sem ordenação: cabeçalho vazio, "Ações", "Ver"... ou th.ec-dt-sem-ordem.
// Colunas sem filtro: th.ec-dt-sem-filtro. Coluna de ações: th.ec-tabela__col-acoes ou "Ações".
// Linhas que não entram (ex.: "nenhum registro"): uma célula com colspan, ou tr.ec-dt-fixa.
// Estilos em resources/sass/_tabelas.scss (.ec-dt*).
// ---------------------------------------------------------------------------

const OPCOES_POR_PAGINA = [10, 25, 50, 100, 0]; // 0 = todos
const CHAVE_POR_PAGINA = 'ec-dt-por-pagina';
const CHAVE_DENSIDADE = 'ec-dt-densidade';
const SEM_ORDEM = /^(a[cç][oõ]es|a[cç][aã]o|entrar|ver|selecionar|editar|excluir)$/i;
const ACOES = /^a[cç][oõ]es$/i;
const MAX_VALORES_FILTRO = 60; // acima disso o filtro vira "contém"

const ICONES = {
    busca: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M11.742 10.344a6.5 6.5 0 1 0-1.397 1.398l3.85 3.85a1 1 0 0 0 1.415-1.414l-3.85-3.85zM12 6.5a5.5 5.5 0 1 1-11 0 5.5 5.5 0 0 1 11 0z"/></svg>',
    filtro: '<svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M1.5 1.5A.5.5 0 0 1 2 1h12a.5.5 0 0 1 .4.8L10 7.7V13.5a.5.5 0 0 1-.276.447l-3 1.5A.5.5 0 0 1 6 15V7.7L1.6 1.8a.5.5 0 0 1-.1-.3z"/></svg>',
    exibicao: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M0 2a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H1a1 1 0 0 1-1-1V2zm1 0v12h4V2H1zm5 0v12h4V2H6zm5 0v12h4V2h-4z"/></svg>',
    exportar: '<svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"/><path d="M7.646 11.854a.5.5 0 0 0 .708 0l3-3a.5.5 0 0 0-.708-.708L8.5 10.293V1.5a.5.5 0 0 0-1 0v8.793L5.354 8.146a.5.5 0 1 0-.708.708l3 3z"/></svg>',
    fechar: '<svg width="10" height="10" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true"><path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854z"/></svg>',
};

let contadorTabelas = 0;

function normalizar(texto) {
    return String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
}

function textoDe(el) {
    return el ? String(el.textContent || '').replace(/\s+/g, ' ').trim() : '';
}

function escapar(texto) {
    return String(texto).replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}

// valor comparável da célula: data, número (formato brasileiro) ou texto
function chaveOrdenacao(texto) {
    const t = String(texto || '').replace(/\s+/g, ' ').trim();
    if (!t || t === '—' || t === '-') return null;

    let m = t.match(/^(\d{2})\/(\d{2})\/(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/);
    if (m) return new Date(m[3], m[2] - 1, m[1], m[4] || 0, m[5] || 0, m[6] || 0).getTime();

    if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?$/.test(t)) return new Date(t.replace(' ', 'T')).getTime();

    const n = t.replace(/^R\$\s*/, '').replace(/%$/, '');
    if (/^-?\d{1,3}(\.\d{3})*(,\d+)?$/.test(n) || /^-?\d+(,\d+)?$/.test(n)) {
        return parseFloat(n.replace(/\./g, '').replace(',', '.'));
    }
    return t;
}

function comparar(a, b) {
    if (a === null && b === null) return 0;
    if (a === null) return 1; // vazios sempre no fim
    if (b === null) return -1;
    if (typeof a === 'number' && typeof b === 'number') return a - b;
    return String(a).localeCompare(String(b), 'pt-BR', { numeric: true, sensitivity: 'base' });
}

// célula na posição visual "indice" considerando colspan
function celulaNaColuna(linha, indice) {
    let pos = 0;
    for (const celula of linha.cells) {
        if (indice < pos + celula.colSpan) return celula;
        pos += celula.colSpan;
    }
    return null;
}

function linhaFixa(linha) {
    return linha.classList.contains('ec-dt-fixa') || (linha.cells.length === 1 && linha.cells[0].colSpan > 1);
}

function lerArmazenado(chave) {
    try { return localStorage.getItem(chave); } catch (e) { return null; }
}

function salvarArmazenado(chave, valor) {
    try { localStorage.setItem(chave, valor); } catch (e) { /* armazenamento indisponível */ }
}

function lerPorPagina(padrao) {
    const salvo = parseInt(lerArmazenado(CHAVE_POR_PAGINA), 10);
    return OPCOES_POR_PAGINA.includes(salvo) ? salvo : padrao;
}

function criar(tag, classe, html) {
    const el = document.createElement(tag);
    if (classe) el.className = classe;
    if (html !== undefined) el.innerHTML = html;
    return el;
}

function botao(classe, html, titulo) {
    const b = criar('button', classe, html);
    b.type = 'button';
    if (titulo) { b.title = titulo; b.setAttribute('aria-label', titulo); }
    return b;
}

// CSV com ";" e BOM: o Excel em português abre direto, com acentos e colunas certas
function baixarCsv(nome, cabecalho, linhas) {
    const celula = v => {
        const t = v === null || v === undefined ? '' : String(v);
        return /[";\n\r]/.test(t) ? '"' + t.replace(/"/g, '""') + '"' : t;
    };
    const conteudo = [cabecalho, ...linhas].map(l => l.map(celula).join(';')).join('\r\n');
    const blob = new Blob(['﻿' + conteudo], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = criar('a');
    const hoje = new Date();
    const data = hoje.getFullYear() + '-' + String(hoje.getMonth() + 1).padStart(2, '0') + '-' + String(hoje.getDate()).padStart(2, '0');
    link.href = url;
    link.download = (normalizar(nome).replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'tabela') + '-' + data + '.csv';
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

class EcDatatable {
    constructor(tabela, opcoes) {
        opcoes = opcoes || {};
        const servidor = !!opcoes.servidor;
        this.tabela = tabela;
        this.opcoes = Object.assign({
            busca: !servidor, ordenar: !servidor, paginar: !servidor, porPagina: 10,
            servidor, colunas: true, ocultas: [], filtros: true, aoFiltrar: null,
            exportar: true, nomeArquivo: null, ferramentas: null, id: null,
        }, opcoes);
        this.servidor = servidor;
        this.filtrosAtivos = this.opcoes.filtros && (!servidor || typeof this.opcoes.aoFiltrar === 'function');

        this.termo = '';
        this.pagina = 1;
        this.porPagina = this.opcoes.paginar ? lerPorPagina(this.opcoes.porPagina) : 0;
        this.ordem = null; // { coluna, direcao: 1 | -1 }
        this.filtros = new Map(); // chave da coluna -> { rotulo, valores: Set } | { rotulo, texto }
        this.colunas = [];
        this.filtradas = [];

        this.numero = ++contadorTabelas;
        tabela.setAttribute('data-ec-dt', this.numero);
        this.chavePreferencias = 'ec-dt:' + (this.opcoes.id ||
            window.location.pathname.replace(/\/\d+(?=\/|$)/g, '/*') + '#' + document.querySelectorAll('table[data-ec-dt]').length);
        this.ajuste = new Set(); // ocultas pelo ajuste automático (não salvas)
        this.ocultas = this.lerOcultas();
        this.densidade = lerArmazenado(CHAVE_DENSIDADE) === 'confortavel' ? 'confortavel' : 'compacta';

        this.estilo = criar('style');
        this.estilo.setAttribute('data-ec-dt-estilo', this.numero);
        document.head.appendChild(this.estilo);

        // contêiner com rolagem (o .table-responsive do template)
        this.corpo = tabela.parentElement.classList.contains('table-responsive') ? tabela.parentElement : tabela;
        this.corpo.classList.add('ec-dt__corpo');
        tabela.classList.add('ec-dt__tabela', 'ec-dt__cartoes');
        this.aplicarDensidade();

        // destino dos botões: contêiner indicado pela tela ou barra própria
        this.destinoFerramentas = this.acharDestino(this.opcoes.ferramentas);
        // modo local: barra + tabela + rodapé formam um cartão; no modo servidor a tela já tem o seu
        if (!this.servidor) this.corpo.classList.add('ec-dt__corpo--cartao');

        this.montarBarra();
        this.montarChips();
        if (!this.servidor) this.montarRodape();
        this.prepararEventos();
        this.aplicar();
    }

    acharDestino(seletor) {
        if (!seletor) return null;
        for (let el = this.tabela.parentElement; el; el = el.parentElement) {
            const destino = el.querySelector(seletor);
            if (destino) return destino;
        }
        return null;
    }

    // ----- preferências ---------------------------------------------------

    // ocultas: padrão da tela ou escolha do usuário. Enquanto o usuário não escolhe, o
    // ajuste automático esconde o que não cabe; depois de escolher (personalizado), a
    // tabela mostra exatamente as colunas marcadas, rolando para o lado se precisar.
    lerOcultas() {
        this.personalizado = false;
        try {
            const salvo = JSON.parse(lerArmazenado(this.chavePreferencias));
            if (salvo && Array.isArray(salvo.ocultas)) {
                this.personalizado = true;
                return new Set(salvo.ocultas);
            }
        } catch (e) { /* preferência inválida: usa o padrão */ }
        return new Set((this.opcoes.ocultas || []).map(normalizar));
    }

    salvarOcultas() {
        salvarArmazenado(this.chavePreferencias, JSON.stringify({ ocultas: Array.from(this.ocultas) }));
    }

    estaOculta(coluna) {
        return coluna.ocultavel && (this.ocultas.has(coluna.chave) || this.ajuste.has(coluna.chave));
    }

    aplicarDensidade() {
        this.tabela.classList.toggle('ec-dt--compacta', this.densidade === 'compacta');
        this.tabela.classList.toggle('ec-dt--confortavel', this.densidade === 'confortavel');
    }

    // ----- montagem ---------------------------------------------------------

    montarBarra() {
        this.barra = criar('div', 'ec-dt__barra');
        if (!this.corpo.classList.contains('ec-dt__corpo--cartao')) this.barra.classList.add('ec-dt__barra--embutida');

        if (this.opcoes.busca) {
            const busca = criar('div', 'ec-busca');
            busca.innerHTML = ICONES.busca.replace('<svg', '<svg class="ec-busca__icone"') +
                '<input type="search" class="form-control form-control-sm ec-busca__campo" placeholder="Buscar na tabela..." aria-label="Buscar na tabela">';
            this.campoBusca = busca.querySelector('input');
            let temporizador = null;
            this.campoBusca.addEventListener('input', () => {
                clearTimeout(temporizador);
                temporizador = setTimeout(() => {
                    this.termo = normalizar(this.campoBusca.value);
                    this.pagina = 1;
                    this.aplicar();
                }, 150);
            });
            this.campoBusca.addEventListener('keyup', e => {
                if (e.key === 'Escape' && this.campoBusca.value) {
                    this.campoBusca.value = '';
                    this.termo = '';
                    this.pagina = 1;
                    this.aplicar();
                }
            });
            this.barra.appendChild(busca);
        }

        this.ferramentas = criar('div', 'ec-dt__ferramentas');
        if (this.opcoes.colunas) {
            this.botaoExibicao = botao('btn btn-sm ec-dt__botao', ICONES.exibicao + '<span>Exibição</span><span class="ec-dt__contador ec-dt--oculta"></span>', 'Colunas e densidade da tabela');
            this.botaoExibicao.setAttribute('aria-haspopup', 'dialog');
            this.botaoExibicao.addEventListener('click', () => this.alternarPainel(this.botaoExibicao, p => this.montarPainelExibicao(p)));
            this.ferramentas.appendChild(this.botaoExibicao);
        }
        if (this.opcoes.exportar) {
            this.botaoExportar = botao('btn btn-sm ec-dt__botao', ICONES.exportar + '<span>Exportar</span>', 'Exportar para Excel (CSV)');
            this.botaoExportar.addEventListener('click', () => this.exportar());
            this.ferramentas.appendChild(this.botaoExportar);
        }

        if (this.opcoes.paginar) {
            const rotulo = criar('label', 'ec-por-pagina');
            rotulo.innerHTML = '<span>Exibir</span><select class="custom-select custom-select-sm">' +
                OPCOES_POR_PAGINA.map(n => `<option value="${n}"${n === this.porPagina ? ' selected' : ''}>${n || 'Todos'}</option>`).join('') +
                '</select>';
            rotulo.querySelector('select').addEventListener('change', e => {
                this.porPagina = parseInt(e.target.value, 10);
                this.pagina = 1;
                salvarArmazenado(CHAVE_POR_PAGINA, this.porPagina);
                this.aplicar();
            });
            this.ferramentas.appendChild(rotulo);
        }

        if (this.destinoFerramentas) {
            this.destinoFerramentas.appendChild(this.ferramentas);
        } else {
            this.barra.appendChild(this.ferramentas);
        }
        this.corpo.parentNode.insertBefore(this.barra, this.corpo);
    }

    montarChips() {
        this.chips = criar('div', 'ec-dt__chips ec-dt--oculta');
        this.chips.setAttribute('aria-label', 'Filtros aplicados');
        this.chips.addEventListener('click', e => {
            const remover = e.target.closest('[data-remover]');
            if (remover) {
                this.filtros.delete(remover.dataset.remover);
                this.aoMudarFiltros();
            } else if (e.target.closest('[data-limpar-filtros]')) {
                this.filtros.clear();
                this.aoMudarFiltros();
            } else {
                const chip = e.target.closest('[data-editar]');
                const coluna = chip && this.colunas.find(c => c.chave === chip.dataset.editar);
                if (coluna) this.abrirFiltro(coluna, chip);
            }
        });
        this.corpo.parentNode.insertBefore(this.chips, this.corpo);
    }

    montarRodape() {
        this.rodape = criar('div', 'ec-dt__rodape');
        this.info = criar('span', 'ec-listagem__info');
        this.navegacao = criar('nav');
        this.navegacao.setAttribute('aria-label', 'Paginação');
        this.rodape.appendChild(this.info);
        this.rodape.appendChild(this.navegacao);
        this.navegacao.addEventListener('click', e => {
            const b = e.target.closest('button[data-pagina]');
            if (!b || b.disabled) return;
            this.pagina = parseInt(b.dataset.pagina, 10);
            this.aplicar();
            this.corpo.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
        });
        this.corpo.parentNode.insertBefore(this.rodape, this.corpo.nextSibling);
    }

    prepararEventos() {
        this.aoClicarCabecalho = e => {
            const th = e.target.closest('th.ec-dt__ordenavel');
            if (!th || e.target.closest('a, button, input, select, label')) return;
            const coluna = parseInt(th.dataset.ecColuna, 10);
            const direcao = this.ordem && this.ordem.coluna === coluna ? -this.ordem.direcao : 1;
            this.ordem = { coluna, direcao };
            this.pagina = 1;
            this.aplicar();
        };
        this.aoTeclarCabecalho = e => {
            if ((e.key === 'Enter' || e.key === ' ') && e.target.matches('th.ec-dt__ordenavel')) {
                e.preventDefault();
                this.aoClicarCabecalho(e);
            }
        };
        // tooltip com o conteúdo completo quando a célula está cortada (calculado só no hover)
        this.aoPassarMouse = e => {
            const td = e.target.closest('td');
            if (!td || !this.tabela.contains(td)) return;
            const cortada = td.scrollWidth > td.clientWidth + 1 ||
                Array.from(td.children).some(f => f.scrollWidth > f.clientWidth + 1);
            if (cortada && (!td.hasAttribute('title') || td.dataset.ecTitulo)) {
                td.title = textoDe(td);
                td.dataset.ecTitulo = '1';
            } else if (!cortada && td.dataset.ecTitulo) {
                td.removeAttribute('title');
                delete td.dataset.ecTitulo;
            }
        };
        this.aoRolarCorpo = () => this.corpo.classList.toggle('ec-dt__corpo--rolado',
            this.corpo.scrollLeft + this.corpo.clientWidth < this.corpo.scrollWidth - 1);

        this.tabela.addEventListener('click', this.aoClicarCabecalho);
        this.tabela.addEventListener('keydown', this.aoTeclarCabecalho);
        this.tabela.addEventListener('mouseover', this.aoPassarMouse);
        this.corpo.addEventListener('scroll', this.aoRolarCorpo, { passive: true });
        window.addEventListener('resize', this.aoRolarCorpo);
        // largura mudou (janela, menu lateral): refaz o ajuste das colunas
        let temporizador = null;
        this.aoRedimensionar = () => {
            clearTimeout(temporizador);
            temporizador = setTimeout(() => this.aplicar(), 150);
        };
        window.addEventListener('resize', this.aoRedimensionar);
        // tabela que aparece depois (aba, bloco recolhido) ou contêiner que muda de largura
        if (window.ResizeObserver) {
            let largura = this.corpo.clientWidth;
            this.observador = new ResizeObserver(() => {
                if (this.corpo.clientWidth !== largura) {
                    largura = this.corpo.clientWidth;
                    this.aoRedimensionar();
                }
            });
            this.observador.observe(this.corpo);
        }
    }

    // ----- colunas ----------------------------------------------------------

    cabecalho() {
        const thead = this.tabela.tHead;
        return thead && thead.rows.length ? thead.rows[thead.rows.length - 1] : null;
    }

    // lê o cabeçalho (refeito a cada atualização: o Vue pode recriar as colunas)
    lerColunas() {
        const linha = this.cabecalho();
        const colunas = [];
        if (!linha) return colunas;
        // linha de amostra: diz quais colunas têm campos (chaves, caixas de marcação...)
        const amostra = this.linhas().find(l => !linhaFixa(l) && !l.classList.contains('ec-tabela__esqueleto'));

        let pos = 0;
        for (const th of linha.cells) {
            const rotulo = Array.from(th.childNodes)
                .filter(n => !(n.classList && n.classList.contains('ec-dt__th-filtro')))
                .map(n => n.textContent).join(' ').replace(/\s+/g, ' ').trim();
            const acoes = th.classList.contains('ec-tabela__col-acoes') || ACOES.test(rotulo);
            const coluna = {
                th, pos, rotulo, acoes,
                chave: normalizar(rotulo) || '#' + pos,
                ocultavel: !acoes && rotulo !== '' && th.colSpan === 1,
            };
            coluna.ordenavel = this.opcoes.ordenar && rotulo !== '' && !SEM_ORDEM.test(rotulo) && !th.classList.contains('ec-dt-sem-ordem');
            const celula = amostra && celulaNaColuna(amostra, pos);
            coluna.editavel = !!(celula && celula.querySelector('input, select, textarea'));
            coluna.filtravel = this.filtrosAtivos && coluna.ocultavel && !coluna.editavel && !SEM_ORDEM.test(rotulo) && !th.classList.contains('ec-dt-sem-filtro');
            colunas.push(coluna);
            pos += th.colSpan;
        }
        return colunas;
    }

    marcarCabecalho() {
        for (const c of this.colunas) {
            const th = c.th;
            if (c.ordenavel) {
                th.classList.add('ec-dt__ordenavel');
                th.dataset.ecColuna = c.pos;
                th.tabIndex = 0;
                const ativa = this.ordem && this.ordem.coluna === c.pos;
                th.setAttribute('aria-sort', ativa ? (this.ordem.direcao === 1 ? 'ascending' : 'descending') : 'none');
            }

            let filtro = th.querySelector(':scope > .ec-dt__th-filtro');
            if (c.filtravel && !filtro) {
                filtro = botao('ec-dt__th-filtro', ICONES.filtro, 'Filtrar ' + c.rotulo);
                filtro.setAttribute('aria-haspopup', 'dialog');
                // não deixa o clique/tecla chegar ao th (que ordenaria a coluna)
                filtro.addEventListener('click', e => {
                    e.stopPropagation();
                    const atual = this.colunas.find(col => col.th === th);
                    if (atual) this.alternarPainel(filtro, p => this.montarPainelFiltro(p, atual));
                });
                filtro.addEventListener('keydown', e => e.stopPropagation());
                th.appendChild(filtro);
            }
            if (filtro) filtro.classList.toggle('ec-dt__th-filtro--ativo', this.filtros.has(c.chave));
        }
    }

    // CSS da tabela: colunas ocultas e coluna de ações fixa à direita
    atualizarEstilo() {
        const base = `table[data-ec-dt="${this.numero}"]`;
        const celulas = k => `${base} > thead > tr > :nth-child(${k}), ${base} > tbody > tr > td:nth-child(${k}):not([colspan]), ${base} > tfoot > tr > :nth-child(${k})`;
        const regras = [];

        const ocultas = this.colunas.filter(c => this.estaOculta(c)).map(c => c.pos + 1);
        if (ocultas.length) regras.push(ocultas.map(celulas).join(',') + '{display:none!important}');

        const ultima = this.colunas[this.colunas.length - 1];
        if (ultima && ultima.acoes && this.colunas.length > 2) {
            const k = ultima.pos + 1;
            regras.push(`@media (min-width:768px){${celulas(k)}{position:sticky;right:0;z-index:1}` +
                `${base} > thead > tr > :nth-child(${k}){z-index:3}` +
                `.ec-dt__corpo--rolado ${base} > * > tr > :nth-child(${k}){box-shadow:-8px 0 10px -8px rgba(8,26,31,.25)}}`);
        }

        const css = regras.join('\n');
        if (this.estilo.textContent !== css) this.estilo.textContent = css;
    }

    linhas() {
        const todas = [];
        for (const tbody of this.tabela.tBodies) todas.push(...tbody.rows);
        return todas;
    }

    textoCelula(linha, coluna) {
        const celula = celulaNaColuna(linha, coluna.pos);
        const texto = textoDe(celula);
        // chave/caixa de marcação sem texto: Sim/Não (exportação)
        const caixa = !texto && celula && celula.querySelector('input[type="checkbox"], input[type="radio"]');
        return caixa ? (caixa.checked ? 'Sim' : 'Não') : texto;
    }

    // ----- aplicação --------------------------------------------------------

    aplicar() {
        if (this.aplicando) return;
        this.aplicando = true;
        try {
            this.colunas = this.lerColunas();
            this.marcarCabecalho();
            this.atualizarEstilo();

            const dados = this.linhas().filter(l => !linhaFixa(l));
            let filtradas = dados;

            if (!this.servidor) {
                if (this.termo) filtradas = filtradas.filter(l => normalizar(l.textContent).includes(this.termo));
                if (this.filtros.size) filtradas = filtradas.filter(l => this.passaFiltros(l));

                if (this.ordem) {
                    const { coluna, direcao } = this.ordem;
                    const chave = new Map(filtradas.map(l => [l, chaveOrdenacao(textoDe(celulaNaColuna(l, coluna)))]));
                    filtradas = filtradas.slice().sort((a, b) => {
                        const ka = chave.get(a), kb = chave.get(b);
                        if (ka === null || kb === null) return comparar(ka, kb); // vazios no fim nas duas direções
                        return comparar(ka, kb) * direcao;
                    });
                    this.reordenar(filtradas);
                }
            }
            this.filtradas = filtradas;

            const total = filtradas.length;
            const porPagina = this.porPagina || total || 1;
            const paginas = Math.max(1, Math.ceil(total / porPagina));
            this.pagina = Math.min(Math.max(1, this.pagina), paginas);
            const inicio = (this.pagina - 1) * porPagina;

            if (!this.servidor) {
                const visiveis = new Set(filtradas.slice(inicio, inicio + porPagina));
                for (const l of dados) l.classList.toggle('ec-dt--oculta', !visiveis.has(l));
            }

            this.ajustarLargura();
            this.rotularCelulas(dados);
            this.atualizarBarraRodape(dados.length, total, inicio, Math.min(inicio + porPagina, total), paginas);
            this.atualizarChips();
            this.aoRolarCorpo();
        } finally {
            this.aplicando = false;
        }
    }

    // Ajuste automático (densidade compacta, telas médias para cima): se a tabela não cabe,
    // oculta colunas a partir da direita (ou pela menor prioridade, th[data-ec-prioridade],
    // 1 = mais importante) até caber. Nunca a primeira coluna, a de ações nem as que o
    // usuário pediu para exibir. As ocultas aparecem desmarcadas no painel "Exibição".
    ajustarLargura() {
        const ativo = !this.personalizado && this.densidade === 'compacta' && window.matchMedia('(min-width: 768px)').matches &&
            this.corpo !== this.tabela && this.linhas().some(l => !linhaFixa(l) && !l.classList.contains('ec-dt--oculta'));
        const antes = Array.from(this.ajuste).join('|');
        this.ajuste.clear();

        if (ativo) {
            const primeira = this.colunas.find(c => !this.ocultas.has(c.chave) && c.rotulo);
            const candidatas = this.colunas
                .filter(c => c.ocultavel && !c.editavel && c !== primeira && !this.ocultas.has(c.chave))
                .sort((a, b) => (parseInt(b.th.dataset.ecPrioridade, 10) || 5) - (parseInt(a.th.dataset.ecPrioridade, 10) || 5) || b.pos - a.pos);
            this.atualizarEstilo();
            const cabe = () => this.tabela.offsetWidth <= this.corpo.clientWidth + 1;
            const escondidas = [];
            for (const c of candidatas) {
                if (cabe()) break;
                this.ajuste.add(c.chave);
                escondidas.push(c.chave);
                this.atualizarEstilo();
            }
            // segunda passada: devolve as que ainda cabem, da mais importante para a menos
            for (const chave of escondidas.reverse()) {
                this.ajuste.delete(chave);
                this.atualizarEstilo();
                if (!cabe()) {
                    this.ajuste.add(chave);
                    this.atualizarEstilo();
                }
            }
        }
        if (Array.from(this.ajuste).join('|') !== antes || !ativo) this.atualizarEstilo();
        // cabe na largura: sem rolagem própria (o cabeçalho fixa no topo da página, ver _tabelas.scss)
        this.corpo.classList.toggle('ec-dt__corpo--cabe', this.corpo !== this.tabela && this.tabela.offsetWidth <= this.corpo.clientWidth + 1);

        if (this.botaoExibicao) {
            const total = this.colunas.filter(c => this.estaOculta(c)).length;
            const contador = this.botaoExibicao.querySelector('.ec-dt__contador');
            contador.textContent = total;
            contador.classList.toggle('ec-dt--oculta', total === 0);
            this.botaoExibicao.title = total ? total + ' coluna(s) oculta(s) — clique para escolher as colunas' : 'Colunas e densidade da tabela';
        }
    }

    // rótulo de cada célula no modo cartão (celular), quando a tela não informou
    rotularCelulas(linhas) {
        for (const l of linhas) {
            if (l.classList.contains('ec-dt--oculta')) continue;
            for (const c of this.colunas) {
                const td = celulaNaColuna(l, c.pos);
                if (td && !td.hasAttribute('data-label') && c.rotulo) td.setAttribute('data-label', c.rotulo);
            }
        }
    }

    passaFiltros(linha) {
        for (const [chave, filtro] of this.filtros) {
            const coluna = this.colunas.find(c => c.chave === chave);
            if (!coluna) continue;
            const texto = this.textoCelula(linha, coluna);
            if (filtro.valores ? !filtro.valores.has(texto) : !normalizar(texto).includes(normalizar(filtro.texto))) return false;
        }
        return true;
    }

    // reordena no DOM só o necessário (dentro de cada tbody), mantendo as linhas fixas no fim
    reordenar(ordenadas) {
        for (const tbody of this.tabela.tBodies) {
            const minhas = ordenadas.filter(l => l.parentNode === tbody);
            const atuais = Array.from(tbody.rows).filter(l => !linhaFixa(l) && minhas.includes(l));
            if (minhas.every((l, i) => atuais[i] === l)) continue;
            const primeiraFixa = Array.from(tbody.rows).find(linhaFixa) || null;
            for (const l of minhas) tbody.insertBefore(l, primeiraFixa);
        }
    }

    atualizarBarraRodape(totalDados, total, inicio, fim, paginas) {
        // tabelas pequenas não precisam de barra: aparece a partir de 6 linhas, com busca ou filtros
        const temFiltro = !!this.termo || this.filtros.size > 0;
        const mostrarBarra = !this.destinoFerramentas || this.opcoes.busca
            ? (this.servidor ? totalDados > 0 || temFiltro : totalDados > 5 || temFiltro)
            : false;
        this.barra.classList.toggle('ec-dt--oculta', !mostrarBarra);
        this.corpo.classList.toggle('ec-dt__corpo--com-barra', mostrarBarra && this.corpo.classList.contains('ec-dt__corpo--cartao'));
        if (this.botaoExportar && !this.exportando) this.botaoExportar.disabled = totalDados === 0;

        if (!this.rodape) return;
        this.rodape.classList.toggle('ec-dt--oculta', totalDados === 0);
        this.corpo.classList.toggle('ec-dt__corpo--com-rodape', totalDados > 0);

        if (total === 0 && temFiltro) {
            this.info.innerHTML = 'Nenhum resultado para a busca e os filtros. ';
            const limpar = botao('btn btn-link btn-sm p-0 align-baseline', 'Limpar tudo');
            limpar.addEventListener('click', () => this.limparTudo());
            this.info.appendChild(limpar);
        } else {
            const filtro = temFiltro ? ` <span class="ec-dt__filtro">(filtrados de ${totalDados})</span>` : '';
            this.info.innerHTML = total
                ? `Mostrando <b>${inicio + 1}–${fim}</b> de <b>${total}</b> registro${total === 1 ? '' : 's'}${filtro}`
                : '';
        }

        if (paginas <= 1) {
            this.navegacao.innerHTML = '';
            return;
        }

        // janela de até 5 páginas em volta da atual
        let de = Math.max(1, this.pagina - 2);
        const ate = Math.min(paginas, de + 4);
        de = Math.max(1, ate - 4);
        const item = (pagina, rotulo, aria, desabilitado, ativo) =>
            `<li class="page-item${ativo ? ' active' : ''}${desabilitado ? ' disabled' : ''}">` +
            `<button type="button" class="page-link" data-pagina="${pagina}" aria-label="${aria}"${ativo ? ' aria-current="page"' : ''}${desabilitado ? ' disabled' : ''}>${rotulo}</button></li>`;

        let html = item(this.pagina - 1, '&lsaquo;', 'Página anterior', this.pagina === 1);
        if (de > 1) html += item(1, '1', 'Página 1') + (de > 2 ? '<li class="page-item disabled"><span class="page-link">…</span></li>' : '');
        for (let p = de; p <= ate; p++) html += item(p, p, 'Página ' + p, false, p === this.pagina);
        if (ate < paginas) html += (ate < paginas - 1 ? '<li class="page-item disabled"><span class="page-link">…</span></li>' : '') + item(paginas, paginas, 'Página ' + paginas);
        html += item(this.pagina + 1, '&rsaquo;', 'Próxima página', this.pagina === paginas);
        this.navegacao.innerHTML = '<ul class="pagination pagination-sm mb-0">' + html + '</ul>';
    }

    limparTudo() {
        this.filtros.clear();
        this.termo = '';
        if (this.campoBusca) this.campoBusca.value = '';
        this.aoMudarFiltros();
    }

    // ----- filtros ----------------------------------------------------------

    aoMudarFiltros() {
        this.pagina = 1;
        this.fecharPainel();
        if (this.servidor && this.opcoes.aoFiltrar) {
            this.opcoes.aoFiltrar(Array.from(this.filtros, ([chave, f]) => ({ chave, rotulo: f.rotulo, texto: f.texto })));
        }
        this.aplicar();
    }

    atualizarChips() {
        if (!this.filtros.size) {
            this.chips.classList.add('ec-dt--oculta');
            this.chips.innerHTML = '';
            return;
        }
        let html = '<span class="ec-dt__chips-rotulo">Filtros:</span>';
        for (const [chave, f] of this.filtros) {
            let valor;
            if (f.valores) {
                const lista = Array.from(f.valores).map(v => v || '(vazio)');
                valor = lista.slice(0, 2).join(', ') + (lista.length > 2 ? ` +${lista.length - 2}` : '');
            } else {
                valor = 'contém "' + f.texto + '"';
            }
            html += `<span class="ec-dt__chip"><button type="button" class="ec-dt__chip-texto" data-editar="${escapar(chave)}" title="Alterar filtro">` +
                `<b>${escapar(f.rotulo)}:</b> ${escapar(valor)}</button>` +
                `<button type="button" class="ec-dt__chip-remover" data-remover="${escapar(chave)}" aria-label="Remover filtro ${escapar(f.rotulo)}" title="Remover filtro">${ICONES.fechar}</button></span>`;
        }
        if (this.filtros.size > 1) html += '<button type="button" class="btn btn-link btn-sm ec-dt__chips-limpar" data-limpar-filtros>Limpar filtros</button>';
        this.chips.innerHTML = html;
        this.chips.classList.remove('ec-dt--oculta');
    }

    abrirFiltro(coluna, ancora) {
        this.alternarPainel(ancora, p => this.montarPainelFiltro(p, coluna), true);
    }

    montarPainelFiltro(painel, coluna) {
        const atual = this.filtros.get(coluna.chave);
        painel.setAttribute('aria-label', 'Filtrar ' + coluna.rotulo);
        painel.innerHTML = `<div class="ec-dt__painel-titulo">Filtrar <b>${escapar(coluna.rotulo)}</b></div>`;

        // valores distintos da coluna (modo local), com quantidade
        let valores = null;
        if (!this.servidor) {
            const contagem = new Map();
            for (const l of this.linhas()) {
                if (linhaFixa(l)) continue;
                const v = this.textoCelula(l, coluna);
                contagem.set(v, (contagem.get(v) || 0) + 1);
            }
            if (contagem.size <= MAX_VALORES_FILTRO) {
                valores = Array.from(contagem).sort((a, b) => comparar(chaveOrdenacao(a[0]), chaveOrdenacao(b[0])));
            }
        }

        const aplicarFiltro = filtro => {
            if (filtro) this.filtros.set(coluna.chave, Object.assign({ rotulo: coluna.rotulo }, filtro));
            else this.filtros.delete(coluna.chave);
            this.aoMudarFiltros();
        };

        const rodape = criar('div', 'ec-dt__painel-rodape');
        const limpar = botao('btn btn-sm btn-outline-secondary', 'Limpar');
        const confirmar = botao('btn btn-sm btn-primary', 'Aplicar');
        rodape.appendChild(limpar);
        rodape.appendChild(confirmar);
        limpar.addEventListener('click', () => aplicarFiltro(null));

        if (valores) {
            const busca = criar('input', 'form-control form-control-sm mb-2');
            busca.type = 'search';
            busca.placeholder = 'Procurar valor...';
            busca.setAttribute('aria-label', 'Procurar valor');
            const todos = criar('label', 'ec-dt__opcao ec-dt__opcao--todos', '<input type="checkbox"> <span>Selecionar todos</span>');
            const lista = criar('div', 'ec-dt__painel-lista');
            const selecionados = atual && atual.valores ? new Set(atual.valores) : new Set(valores.map(v => v[0]));
            lista.innerHTML = valores.map(([v, qtd], i) =>
                `<label class="ec-dt__opcao"><input type="checkbox" data-i="${i}"${selecionados.has(v) ? ' checked' : ''}> ` +
                `<span class="ec-dt__opcao-texto">${v ? escapar(v) : '<i>(vazio)</i>'}</span><span class="ec-dt__opcao-qtd">${qtd}</span></label>`).join('');
            const caixas = () => Array.from(lista.querySelectorAll('input'));
            const sincronizarTodos = () => {
                const visiveis = caixas().filter(c => !c.closest('label').classList.contains('ec-dt--oculta'));
                const marcados = visiveis.filter(c => c.checked).length;
                todos.querySelector('input').checked = marcados === visiveis.length && marcados > 0;
                todos.querySelector('input').indeterminate = marcados > 0 && marcados < visiveis.length;
                confirmar.disabled = !caixas().some(c => c.checked);
            };
            busca.addEventListener('input', () => {
                const termo = normalizar(busca.value);
                for (const c of caixas()) c.closest('label').classList.toggle('ec-dt--oculta', !normalizar(valores[c.dataset.i][0]).includes(termo));
                sincronizarTodos();
            });
            busca.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); confirmar.click(); } });
            todos.querySelector('input').addEventListener('change', e => {
                for (const c of caixas()) if (!c.closest('label').classList.contains('ec-dt--oculta')) c.checked = e.target.checked;
                sincronizarTodos();
            });
            lista.addEventListener('change', sincronizarTodos);
            confirmar.addEventListener('click', () => {
                const marcados = caixas().filter(c => c.checked).map(c => valores[c.dataset.i][0]);
                aplicarFiltro(marcados.length === valores.length ? null : { valores: new Set(marcados) });
            });
            if (valores.length > 8) painel.appendChild(busca);
            painel.appendChild(todos);
            painel.appendChild(lista);
            sincronizarTodos();
        } else {
            const campo = criar('input', 'form-control form-control-sm');
            campo.type = 'search';
            campo.placeholder = 'Contém...';
            campo.setAttribute('aria-label', 'Texto que a coluna ' + coluna.rotulo + ' contém');
            campo.value = atual && atual.texto ? atual.texto : '';
            const aplicarTexto = () => {
                const texto = campo.value.trim();
                aplicarFiltro(texto ? { texto } : null);
            };
            campo.addEventListener('keydown', e => { if (e.key === 'Enter') { e.preventDefault(); aplicarTexto(); } });
            confirmar.addEventListener('click', aplicarTexto);
            painel.appendChild(campo);
        }
        painel.appendChild(rodape);
    }

    // ----- exibição (colunas + densidade) ----------------------------------

    // o painel é atualizado no lugar (sem recriar), para não mudar o que está sob o cursor
    montarPainelExibicao(painel) {
        painel.setAttribute('aria-label', 'Exibição da tabela');

        const densidade = criar('div', 'ec-dt__painel-secao');
        densidade.innerHTML = '<div class="ec-dt__painel-titulo">Linhas</div>' +
            '<div class="btn-group btn-group-sm w-100" role="group" aria-label="Densidade das linhas">' +
            '<button type="button" class="btn btn-outline-primary" data-densidade="compacta">Compactas</button>' +
            '<button type="button" class="btn btn-outline-primary" data-densidade="confortavel">Confortáveis</button>' +
            '</div><small class="ec-dt__painel-dica" data-dica-densidade></small>';
        painel.appendChild(densidade);

        const secao = criar('div', 'ec-dt__painel-secao');
        secao.innerHTML = '<div class="ec-dt__painel-titulo">Colunas visíveis</div>' +
            '<div class="ec-dt__painel-lista"></div><small class="ec-dt__painel-dica" data-dica-colunas></small>';
        const lista = secao.querySelector('.ec-dt__painel-lista');
        const restaurar = botao('btn btn-link btn-sm p-0 mt-2', 'Restaurar padrão (ajuste automático)');
        secao.appendChild(restaurar);
        painel.appendChild(secao);

        const atualizar = () => {
            for (const b of densidade.querySelectorAll('[data-densidade]')) {
                const ativa = b.dataset.densidade === this.densidade;
                b.classList.toggle('active', ativa);
                b.setAttribute('aria-pressed', ativa);
            }
            densidade.querySelector('[data-dica-densidade]').textContent = this.densidade === 'compacta'
                ? 'Uma linha por registro; passe o mouse para ver o texto completo.'
                : 'Textos longos quebram em várias linhas.';

            const ocultaveis = this.colunas.filter(c => c.ocultavel);
            secao.hidden = !ocultaveis.length;
            const rolagem = lista.scrollTop;
            lista.innerHTML = ocultaveis.map(c =>
                `<label class="ec-dt__opcao"><input type="checkbox" data-chave="${escapar(c.chave)}"${this.estaOculta(c) ? '' : ' checked'}> <span class="ec-dt__opcao-texto">${escapar(c.rotulo)}</span>` +
                (this.ajuste.has(c.chave) ? '<span class="ec-dt__opcao-qtd" title="Oculta automaticamente porque não coube na largura da tela">não coube</span>' : '') + '</label>').join('');
            lista.scrollTop = rolagem;
            secao.querySelector('[data-dica-colunas]').textContent = this.personalizado
                ? 'A tabela mostra as colunas marcadas; se não couberem, role para o lado.'
                : 'As colunas que não cabem na largura da tela ficam ocultas automaticamente.';
            restaurar.hidden = !this.personalizado;
        };

        densidade.addEventListener('click', e => {
            const b = e.target.closest('[data-densidade]');
            if (!b || b.dataset.densidade === this.densidade) return;
            this.densidade = b.dataset.densidade;
            salvarArmazenado(CHAVE_DENSIDADE, this.densidade);
            this.aplicarDensidade();
            this.aplicar();
            atualizar();
        });

        lista.addEventListener('change', e => {
            const caixa = e.target;
            const chave = caixa.dataset.chave;
            // primeira escolha: parte do que está na tela (inclusive o que o ajuste escondeu)
            if (!this.personalizado) {
                this.personalizado = true;
                for (const c of this.ajuste) this.ocultas.add(c);
                this.ajuste.clear();
            }
            if (caixa.checked) this.ocultas.delete(chave);
            else this.ocultas.add(chave);
            // ao menos uma coluna visível
            if (!this.colunas.some(c => c.ocultavel && !this.ocultas.has(c.chave))) this.ocultas.delete(chave);
            this.salvarOcultas();
            this.aplicar();
            atualizar();
        });

        restaurar.addEventListener('click', () => {
            this.ocultas = new Set((this.opcoes.ocultas || []).map(normalizar));
            this.personalizado = false;
            salvarArmazenado(this.chavePreferencias, '');
            this.aplicar();
            atualizar();
        });

        atualizar();
    }

    // ----- painel flutuante (exibição e filtros) ---------------------------

    alternarPainel(ancora, montar, forcarAbrir) {
        if (this.painel && this.painel.ancora === ancora && !forcarAbrir) {
            this.fecharPainel();
            return;
        }
        this.fecharPainel();

        const painel = criar('div', 'ec-dt__painel');
        painel.setAttribute('role', 'dialog');
        montar(painel);
        document.body.appendChild(painel);
        this.painel = { el: painel, ancora, montar };
        ancora.setAttribute('aria-expanded', 'true');
        this.posicionarPainel();

        this.aoClicarFora = e => {
            if (!painel.contains(e.target) && !ancora.contains(e.target)) this.fecharPainel();
        };
        this.aoTeclarPainel = e => {
            if (e.key === 'Escape') { this.fecharPainel(); ancora.focus(); }
        };
        this.aoReposicionar = () => this.posicionarPainel();
        document.addEventListener('mousedown', this.aoClicarFora);
        document.addEventListener('keydown', this.aoTeclarPainel);
        window.addEventListener('resize', this.aoReposicionar);

        const foco = painel.querySelector('input[type="search"]') || painel.querySelector('input, button');
        if (foco) foco.focus({ preventScroll: true });
    }

    posicionarPainel() {
        if (!this.painel) return;
        const { el, ancora } = this.painel;
        const r = ancora.getBoundingClientRect();
        const largura = el.offsetWidth;
        const limite = document.documentElement.clientWidth;
        // abre para baixo; sem espaço, para cima; e nunca passa da altura da janela
        const abaixo = window.innerHeight - r.bottom - 12;
        const acima = r.top - 12;
        const paraCima = abaixo < 220 && acima > abaixo;
        el.style.maxHeight = Math.max(160, paraCima ? acima : abaixo) + 'px';
        el.style.top = (paraCima ? r.top + window.scrollY - 6 - Math.min(el.scrollHeight, Math.max(160, acima)) : r.bottom + window.scrollY + 6) + 'px';
        el.style.left = (Math.max(8, Math.min(r.left, limite - largura - 8)) + window.scrollX) + 'px';
    }

    fecharPainel() {
        if (!this.painel) return;
        this.painel.ancora.setAttribute('aria-expanded', 'false');
        this.painel.el.remove();
        this.painel = null;
        document.removeEventListener('mousedown', this.aoClicarFora);
        document.removeEventListener('keydown', this.aoTeclarPainel);
        window.removeEventListener('resize', this.aoReposicionar);
    }

    // ----- exportação ---------------------------------------------------------

    async exportar() {
        const visiveis = this.colunas.filter(c => !c.acoes && c.rotulo && !(c.ocultavel && this.ocultas.has(c.chave)));
        // as colunas que só o ajuste automático escondeu continuam na planilha
        const original = this.botaoExportar.innerHTML;
        this.exportando = true;
        this.botaoExportar.disabled = true;
        this.botaoExportar.innerHTML = '<span class="spinner-border spinner-border-sm" aria-hidden="true"></span><span>Exportando...</span>';
        try {
            let cabecalho, linhas;
            if (typeof this.opcoes.exportar === 'function') {
                ({ cabecalho, linhas } = await this.opcoes.exportar({ colunas: visiveis.map(c => c.rotulo) }));
            } else {
                cabecalho = visiveis.map(c => c.rotulo);
                const fonte = this.servidor ? this.linhas().filter(l => !linhaFixa(l)) : this.filtradas;
                linhas = fonte.map(l => visiveis.map(c => this.textoCelula(l, c)));
            }
            baixarCsv(this.opcoes.nomeArquivo || document.title, cabecalho, linhas);
        } catch (erro) {
            console.log(erro);
            window.alert('Não foi possível exportar a tabela. Tente novamente.');
        } finally {
            this.exportando = false;
            this.botaoExportar.innerHTML = original;
            this.botaoExportar.disabled = false;
        }
    }

    destruir() {
        this.fecharPainel();
        this.tabela.removeEventListener('click', this.aoClicarCabecalho);
        this.tabela.removeEventListener('keydown', this.aoTeclarCabecalho);
        this.tabela.removeEventListener('mouseover', this.aoPassarMouse);
        this.corpo.removeEventListener('scroll', this.aoRolarCorpo);
        window.removeEventListener('resize', this.aoRolarCorpo);
        window.removeEventListener('resize', this.aoRedimensionar);
        if (this.observador) this.observador.disconnect();
        this.barra.remove();
        this.ferramentas.remove();
        this.chips.remove();
        if (this.rodape) this.rodape.remove();
        this.estilo.remove();
    }
}

export default {
    inserted(el, binding) {
        el.__ecDatatable = new EcDatatable(el, binding.value);
    },
    componentUpdated(el) {
        // o Vue acabou de atualizar as linhas: refaz busca/filtros/ordem/página sobre elas
        if (el.__ecDatatable) el.__ecDatatable.aplicar();
    },
    unbind(el) {
        if (el.__ecDatatable) el.__ecDatatable.destruir();
        delete el.__ecDatatable;
    },
};
